import { useCallback, useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { ResourceCatalogComponent } from '../../catalog/types';
import { InfoCard } from '@backstage/core-components';
import { DevHubTypography } from '@devxdiscover/devhub-ui';
import { ComponentsTable } from './ComponentsTable';
import { getEntityComponents } from '../FetchEntityComponents';
import { useApi } from '@backstage/core-plugin-api';

const GET_COMPONENT_INFO_FROM_ID = gql`
  query Resources(
    $pagination: PaginationInput
    $filter: ResourceCatalogComponentFilter
  ) {
    resources {
      componentsPaged(pagination: $pagination, filter: $filter) {
        totalCount
        components {
          description
          devHubOwner
          id
          lifecycle
          name
          scmSlug
          source
          sourceLocationUrl
          title
          type
        }
      }
    }
  }
`;

const PAGE_LIMIT = 100;

/**
 * Content component for application components content
 */
export const ComponentsContent = () => {
  const { entity } = useEntity();
  const [componentIds, setComponentIds] = useState<string[]>([]);
  const [components, setComponents] = useState<ResourceCatalogComponent[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();
  const [isLoadingAll, setIsLoadingAll] = useState<boolean>(false);
  const catalogApi = useApi(catalogApiRef);

  const [fetchData] = useLazyQuery(GET_COMPONENT_INFO_FROM_ID, {
    fetchPolicy: 'network-only',
  });

  const handlePageChange = useCallback(
    (_page: number, _rowsPerPage: number) => {
      // No-op since we're loading all data upfront
    },
    [],
  );

  // Get the component IDs from the entity relations
  useEffect(() => {
    const fetchComponentIds = async () => {
      const { entityComponentIds } = await getEntityComponents(
        entity,
        catalogApi,
        'exclude',
        ['docs'],
      );
      setComponentIds(entityComponentIds);
    };

    fetchComponentIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity]);

  // Fetch all components when componentIds are available
  useEffect(() => {
    if (componentIds.length === 0 || !fetchData) return;

    const fetchAllComponents = async () => {
      setIsLoadingAll(true);

      try {
        // Fetch first page to get total count
        const result = await fetchData({
          variables: {
            pagination: { limit: PAGE_LIMIT, offset: 0 },
            filter: { ids: componentIds },
          },
        });

        const componentsPaged = result.data?.resources?.componentsPaged;
        const fetchedTotalCount = componentsPaged?.totalCount ?? 0;
        const firstPageComponents = componentsPaged?.components ?? [];

        setTotalCount(fetchedTotalCount);

        if (fetchedTotalCount === 0) {
          setComponents([]);
          setIsLoadingAll(false);
          return;
        }

        // If we have 100 or fewer, we're done
        if (fetchedTotalCount <= PAGE_LIMIT) {
          setComponents(firstPageComponents);
          setIsLoadingAll(false);
          return;
        }

        // Need to fetch more pages - create array of offsets
        const offsets = [];
        for (let i = PAGE_LIMIT; i < fetchedTotalCount; i += PAGE_LIMIT) {
          offsets.push(i);
        }

        // Fetch all remaining pages in parallel
        const results = await Promise.all(
          offsets.map(offset =>
            fetchData({
              variables: {
                pagination: { limit: PAGE_LIMIT, offset },
                filter: { ids: componentIds },
              },
            }),
          ),
        );

        // Combine all components
        const moreComponents = results.flatMap(
          moreData =>
            moreData.data?.resources?.componentsPaged?.components ?? [],
        );

        setComponents([...firstPageComponents, ...moreComponents]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching components:', error);
      } finally {
        setIsLoadingAll(false);
      }
    };

    fetchAllComponents();
  }, [componentIds, fetchData]);

  return (
    <>
      <InfoCard title="Components">
        {totalCount && (
          <DevHubTypography
            variant="subtitle1"
            style={{ paddingBottom: '10px' }}
          >
            {totalCount} {totalCount === 1 ? 'component' : 'components'}{' '}
            associated with {entity.metadata.title}
          </DevHubTypography>
        )}
        {!isLoadingAll && components.length === 0 ? (
          <DevHubTypography variant="body1">
            No components associated with {entity.metadata.title}
          </DevHubTypography>
        ) : (
          <ComponentsTable
            components={components}
            loading={isLoadingAll}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        )}
      </InfoCard>
    </>
  );
};
